<script setup lang="ts">
import {computed, ref} from "vue";
import {useRouter} from "vue-router";
import {ChevronLeft, Pencil, ScanQrCode} from "@lucide/vue";
import BaseContainer from "@/core/components/base/BaseContainer.vue";
import BaseFlexSpinner from "@/core/components/base/spinner/BaseFlexSpinner.vue";
import {Button} from "@/core/components/ui/button";
import GiftKpiCards from "@/birthday-gift/components/statistics/GiftKpiCards.vue";
import GiftScansTable from "@/birthday-gift/components/statistics/GiftScansTable.vue";
import GiftScanDialog from "@/birthday-gift/components/scan/GiftScanDialog.vue";
import useBirthdayGifts from "@/birthday-gift/composables/useBirthdayGifts";
import useGiftStatistics from "@/birthday-gift/composables/useGiftStatistics";

const props = defineProps<{
    giftId: string;
}>();

const router = useRouter();

const id = computed(() => Number(props.giftId));

const {data: gifts} = useBirthdayGifts();
const {statistics, scans} = useGiftStatistics(id);

const gift = computed(() => (gifts.value ?? []).find((item) => item.id === id.value));

const scanOpen = ref(false);

const backToList = () => router.push({
    name: 'patient-administration.communication.birthday-gifts',
});

const editGift = () => router.push({
    name: 'patient-administration.communication.birthday-gifts',
    query: {edit: id.value},
});

/** A redemption changes both the counters and the scan list. */
const onRedeemed = () => {
    statistics.mutate();
    scans.mutate();
};
</script>

<template>
  <BaseContainer class="p-4">
    <Button
      variant="link"
      class="self-start px-0"
      @click="backToList"
    >
      <ChevronLeft class="h-4 w-4" />Tous les cadeaux
    </Button>

    <div class="flex flex-wrap items-end justify-between gap-4">
      <div class="min-w-0">
        <h1 class="mb-1 mt-1 text-2xl font-medium">
          {{ gift?.name ?? 'Cadeau' }}
        </h1>
        <p class="text-sm text-muted-foreground">
          Utilisation du QR code · 30 derniers jours
        </p>
      </div>
      <div class="flex items-center gap-2.5">
        <Button
          class="rounded-full bg-secondary text-primary-dark hover:bg-secondary-dark"
          @click="scanOpen = true"
        >
          <ScanQrCode class="h-4 w-4" />Scanner un QR code
        </Button>
        <Button
          variant="outline"
          @click="editGift"
        >
          <Pencil class="h-4 w-4" />Modifier ce cadeau
        </Button>
      </div>
    </div>

    <BaseFlexSpinner v-if="statistics.isLoading.value" />
    <template v-else>
      <GiftKpiCards
        class="mt-4"
        :statistics="statistics.data.value"
      />

      <div class="mt-4 overflow-hidden rounded-lg bg-white shadow">
        <div class="flex flex-wrap items-center justify-between gap-3 border-b border-gray-light px-5 py-3.5">
          <h2 class="text-lg font-medium">
            QR codes scannés au comptoir
          </h2>
          <span class="text-xs text-muted-foreground">Patient · date d'anniversaire · âge</span>
        </div>

        <GiftScansTable :scans="scans.data.value?.data ?? []" />
      </div>
    </template>

    <GiftScanDialog
      v-model:open="scanOpen"
      @redeemed="onRedeemed"
    />
  </BaseContainer>
</template>
